import { Form, Head } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import OnboardingAddressFieldset from "@/components/onboarding/onboarding-address-fieldset"
import { CharterSheet } from "@/components/signup/charter-sheet"
import { TiktokIdentityHeader } from "@/components/signup/tiktok-identity-header"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { useOnline } from "@/hooks/use-online"
import PublicLayout from "@/layouts/public-layout"
import { initI18n } from "@/lib/i18n/client"
import { resolvePublicLocale } from "@/lib/i18n-public"
import { signupDetailsPath } from "@/routes"

export interface SignupDetailsBrand {
  name: string | null
  logo_url: string | null
  locale: "en" | "fr"
}

export interface SignupDetailsIdentity {
  display_name: string | null
  handle: string | null
  avatar_url: string | null
  follower_count: number
}

export interface SignupDetailsCharter {
  rich_text_html: string
  is_default: boolean
}

export interface SignupDetailsProps {
  /** Opaque cache-bridge id — round-trips as a hidden field so the POST
   *  re-reads the verified TikTok payload + invite. */
  handoff: string
  brand: SignupDetailsBrand
  identity: SignupDetailsIdentity
  charter: SignupDetailsCharter
  /** Echoed values on validation re-render; first render prefills the
   *  recipient from the TikTok name + country = FR. */
  form_values: {
    user?: { email?: string | null; phone?: string | null }
    shipping_address?: {
      recipient_name?: string | null
      street?: string | null
      street_line_2?: string | null
      postal_code?: string | null
      city?: string | null
      country?: string | null
    }
  }
  /** Optional — the BE deliberately does NOT ship a literal `errors` prop
   *  (it would shallow-override Inertia's injected `session[:inertia_errors]`
   *  bag and swallow inline validation). The Inertia `<Form>` render-prop
   *  `errors` is the surface; this stays only for test-fixture convenience. */
  errors?: Record<string, string[] | undefined>
}

/**
 * Signups/Details — OKS-466 combined onboarding screen (UX Spec §C).
 *
 * ONE screen: TikTok identity header → shipping address + country
 * (default France) → phone (required) → email (manual, required) →
 * charter acceptance (gates Finish). Passwordless — there is NO password
 * field anywhere; the BE generates the account secret. TikTok is the
 * credential.
 *
 * **Inertia `<Form>`** from `@inertiajs/react` — render-prop `errors`
 * surfaces flat dotted keys (`user.email`, `user.phone`,
 * `shipping_address.<f>`, `acceptance.charter`). NO shadcn Form /
 * react-hook-form / zod (Crew hard rule).
 *
 * **Wire format** (BE-locked):
 *   - `name="user[email]"`, `name="user[phone]"`
 *   - `name="shipping_address[…]"` (via `<OnboardingAddressFieldset>`)
 *   - `name="acceptance[charter]"` (hidden bridge — Radix Checkbox is
 *     controlled for UX; the hidden input carries the wire value)
 *   - hidden `name="handoff"` (top-level cache id)
 *   - NO `name="user[password]"`, NO `name="acceptance[tos]"`
 */
export default function SignupDetails({
  handoff,
  brand,
  identity,
  charter,
  form_values,
}: SignupDetailsProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [charterAccepted, setCharterAccepted] = useState(false)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  // OKS-540 — re-init i18n with the resolved public locale on mount so the
  // client render matches the server's brand-derived locale (brand wins over
  // navigator; `?locale=`/`?lang=` still outrank). Mirrors InviteLanding.tsx.
  useEffect(() => {
    const locale = resolvePublicLocale({ brandLocale: brand.locale })
    initI18n({ locale })
  }, [brand.locale])

  const brandName = brand.name ?? ""

  // BE may echo nulls for un-typed fields; the shared address fieldset
  // expects `string | undefined`, so coerce null → undefined.
  const shippingValues = Object.fromEntries(
    Object.entries(form_values.shipping_address ?? {}).map(([k, v]) => [
      k,
      v ?? undefined,
    ]),
  )

  return (
    <PublicLayout>
      <Head>
        <title>{t("signups.details.head_title")}</title>
      </Head>
      <div
        data-testid="signup-details"
        data-state="default"
        className="bg-background mx-auto flex min-h-svh w-full max-w-md flex-1 flex-col gap-6 px-6 py-8"
      >
        <TiktokIdentityHeader
          displayName={identity.display_name}
          handle={identity.handle}
          avatarUrl={identity.avatar_url}
          followerCount={identity.follower_count}
        />

        <div className="flex flex-col gap-1">
          <h1
            ref={headingRef}
            tabIndex={-1}
            data-testid="signup-details-heading"
            className="text-2xl font-bold tracking-tight outline-none"
          >
            {t("signups.details.heading")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("signups.details.subtitle")}
          </p>
        </div>

        <Form
          method="post"
          action={signupDetailsPath({ handoff })}
          className="flex flex-col gap-4"
          data-testid="signup-details-form"
        >
          {({ processing, errors }) => {
            const baseErrors = errors.base
            const emailErrors = errors["user.email"]
            const phoneErrors = errors["user.phone"]
            const charterErrors = errors["acceptance.charter"]
            const submitDisabled = processing || !online || !charterAccepted
            return (
              <>
                {/* Hidden cache-bridge id — top-level (NOT user[…]). */}
                <input type="hidden" name="handoff" value={handoff} />

                {Array.isArray(baseErrors) && baseErrors.length > 0 && (
                  <Alert
                    variant="destructive"
                    data-testid="signup-details-error-base"
                  >
                    <AlertDescription>{baseErrors.join(", ")}</AlertDescription>
                  </Alert>
                )}

                <OnboardingAddressFieldset
                  values={shippingValues}
                  errors={errors as Record<string, string[] | undefined>}
                  disabled={processing}
                />

                <div className="grid gap-2">
                  <Label htmlFor="signup-details-phone">
                    {t("signups.details.phone_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <Input
                    id="signup-details-phone"
                    name="user[phone]"
                    type="tel"
                    autoComplete="tel"
                    aria-required="true"
                    defaultValue={form_values.user?.phone ?? ""}
                    placeholder={t("signups.details.phone_placeholder")}
                    aria-invalid={phoneErrors ? "true" : undefined}
                    aria-describedby={
                      phoneErrors ? "signup-details-error-phone" : undefined
                    }
                    data-testid="signup-details-phone"
                  />
                  <InputError
                    id="signup-details-error-phone"
                    messages={phoneErrors}
                    data-testid="signup-details-error-phone"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="signup-details-email">
                    {t("signups.details.email_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <Input
                    id="signup-details-email"
                    name="user[email]"
                    type="email"
                    autoComplete="email"
                    aria-required="true"
                    defaultValue={form_values.user?.email ?? ""}
                    placeholder={t("signups.details.email_placeholder")}
                    aria-invalid={emailErrors ? "true" : undefined}
                    aria-describedby={
                      emailErrors ? "signup-details-error-email" : undefined
                    }
                    data-testid="signup-details-email"
                  />
                  <InputError
                    id="signup-details-error-email"
                    messages={emailErrors}
                    data-testid="signup-details-error-email"
                  />
                </div>

                {/* Charter acceptance — controlled Checkbox for UX + a
                    hidden input carrying the wire value (`acceptance[charter]`).
                    Mirrors the country-select hidden-input bridge so the
                    Vitest wire canary asserts a literal `name`. */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="signup-details-charter"
                      checked={charterAccepted}
                      onCheckedChange={(next) =>
                        setCharterAccepted(next === true)
                      }
                      aria-describedby={
                        charterErrors
                          ? "signup-details-error-charter"
                          : undefined
                      }
                      data-testid="signup-details-charter"
                    />
                    <input
                      type="hidden"
                      name="acceptance[charter]"
                      value={charterAccepted ? "true" : "false"}
                      data-testid="signup-details-charter-hidden"
                    />
                    <Label
                      htmlFor="signup-details-charter"
                      className="text-sm leading-snug font-normal"
                    >
                      {t("signups.details.charter.checkbox", {
                        brand: brandName,
                      })}
                    </Label>
                  </div>
                  <CharterSheet
                    brandName={brandName}
                    charterHtml={charter.rich_text_html}
                  />
                  <InputError
                    id="signup-details-error-charter"
                    messages={charterErrors}
                    data-testid="signup-details-error-charter"
                  />
                </div>

                {!online && (
                  <p
                    className="text-muted-foreground text-sm"
                    role="status"
                    aria-live="polite"
                    data-testid="signup-details-offline-helper"
                  >
                    {t("signups.details.errors.offline_helper")}
                  </p>
                )}

                <div className="bg-background sticky bottom-0 -mx-6 mt-2 flex flex-col gap-3 border-t px-6 py-4 sm:static sm:mx-0 sm:p-0">
                  <Button
                    type="submit"
                    disabled={submitDisabled}
                    aria-disabled={submitDisabled}
                    data-testid="signup-details-submit"
                    size="lg"
                    className="w-full"
                  >
                    {processing && <Spinner className="size-4" />}
                    {processing
                      ? t("signups.details.finish_processing")
                      : t("signups.details.finish")}
                  </Button>
                </div>
              </>
            )
          }}
        </Form>
      </div>
    </PublicLayout>
  )
}
